/**
 * FunnelModeContent Component
 *
 * Container for all funnel mode UI in AnalysisBuilder.
 * Displays tabs for Steps and Display configuration.
 */

import { memo, useState } from 'react'
import type { CubeMeta, FunnelBindingKey, FunnelStepState, ChartType, ChartDisplayConfig } from '../../types.js'
import type { FunnelPanelTab } from './types.js'
import type { ColorPalette } from '../../utils/colorPalettes.js'
import FunnelConfigPanel from './FunnelConfigPanel.js'
import FunnelStepList from './FunnelStepList.js'
import AnalysisDisplayConfigPanel from './AnalysisDisplayConfigPanel.js'
import { useTranslation } from '../../hooks/useTranslation.js'

export interface FunnelModeContentProps {
  /** Currently selected cube for funnel */
  funnelCube: string | null
  /** Current funnel steps */
  funnelSteps: FunnelStepState[]
  /** Index of the currently active step */
  activeFunnelStepIndex: number
  /** Time dimension for funnel temporal ordering */
  funnelTimeDimension: string | null
  /** Binding key that links steps together */
  funnelBindingKey: FunnelBindingKey | null
  /** Cube metadata for field selection */
  schema: CubeMeta | null

  // Actions - Steps
  /** Callback when cube changes */
  onCubeChange: (cube: string | null) => void
  /** Add a new funnel step */
  onAddStep: () => void
  /** Remove a funnel step by index */
  onRemoveStep: (index: number) => void
  /** Update a funnel step */
  onUpdateStep: (index: number, updates: Partial<FunnelStepState>) => void
  /** Set the active step index */
  onSelectStep: (index: number) => void
  /** Reorder funnel steps */
  onReorderSteps: (fromIndex: number, toIndex: number) => void
  /** Set the time dimension */
  onTimeDimensionChange: (dimension: string | null) => void
  /** Set the binding key */
  onBindingKeyChange: (bindingKey: FunnelBindingKey | null) => void

  // Display configuration (optional - for Display tab)
  /** Chart type for funnel display */
  chartType?: ChartType
  /** Display configuration */
  displayConfig?: ChartDisplayConfig
  /** Color palette */
  colorPalette?: ColorPalette
  /** Callback when display config changes */
  onDisplayConfigChange?: (config: ChartDisplayConfig) => void
}

/**
 * FunnelModeContent displays the complete funnel configuration interface:
 * - Tabs: Steps | Display
 * - Steps tab: Config panel (binding key + time dimension) + step list
 * - Display tab: Funnel chart display options
 */
const FunnelModeContent = memo(function FunnelModeContent({
  funnelCube,
  funnelSteps,
  activeFunnelStepIndex,
  funnelTimeDimension,
  funnelBindingKey,
  schema,
  onCubeChange,
  onAddStep,
  onRemoveStep,
  onUpdateStep,
  onSelectStep,
  onReorderSteps,
  onTimeDimensionChange,
  onBindingKeyChange,
  // Display props
  chartType = 'funnel',
  displayConfig,
  colorPalette,
  onDisplayConfigChange,
}: FunnelModeContentProps) {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<FunnelPanelTab>('steps')

  // Check if display tab is available
  const hasDisplayTab = displayConfig && onDisplayConfigChange

  return (
    <div className="dc:flex dc:flex-col dc:h-full">
      {/* Tab Bar */}
      <div className="dc:border-b border-dc-border dc:flex-shrink-0 dc:overflow-x-auto dc:overflow-y-hidden scrollbar-thin">
        <div className="dc:flex dc:min-w-max">
          <button
            onClick={() => setActiveTab('steps')}
            className={`dc:flex-1 dc:px-4 dc:py-3 dc:text-sm dc:font-medium dc:transition-colors dc:whitespace-nowrap ${
              activeTab === 'steps'
                ? 'text-dc-primary dc:border-b-2 border-dc-primary'
                : 'text-dc-text-secondary hover:text-dc-text'
            }`}
          >
            {t('funnel.tabs.steps')}
          </button>
          <button
            onClick={() => hasDisplayTab && setActiveTab('display')}
            disabled={!hasDisplayTab}
            className={`dc:flex-1 dc:px-4 dc:py-3 dc:text-sm dc:font-medium dc:transition-colors dc:whitespace-nowrap ${
              activeTab === 'display'
                ? 'text-dc-primary dc:border-b-2 border-dc-primary'
                : !hasDisplayTab
                  ? 'text-dc-text-muted dc:cursor-not-allowed dc:opacity-50'
                  : 'text-dc-text-secondary hover:text-dc-text'
            }`}
            title={!hasDisplayTab ? t('funnel.tabs.displayUnavailable') : t('funnel.tabs.displayTitle')}
          >
            {t('funnel.tabs.display')}
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'steps' ? (
        <div className="dc:flex dc:flex-col dc:flex-1 dc:min-h-0">
          {/* Configuration Panel - Cube + Binding Key + Time Dimension */}
          <FunnelConfigPanel
            selectedCube={funnelCube}
            bindingKey={funnelBindingKey}
            timeDimension={funnelTimeDimension}
            schema={schema}
            onCubeChange={onCubeChange}
            onBindingKeyChange={onBindingKeyChange}
            onTimeDimensionChange={onTimeDimensionChange}
          />

          {/* Step List - scrollable with extra bottom padding for "Add step" button */}
          <div className="dc:flex-1 dc:min-h-0 dc:overflow-auto dc:p-4 dc:pb-24">
            <FunnelStepList
              steps={funnelSteps}
              activeStepIndex={activeFunnelStepIndex}
              schema={schema}
              onAddStep={onAddStep}
              onRemoveStep={onRemoveStep}
              onUpdateStep={onUpdateStep}
              onSelectStep={onSelectStep}
              onReorderSteps={onReorderSteps}
            />
          </div>
        </div>
      ) : activeTab === 'display' && displayConfig && onDisplayConfigChange ? (
        <div className="dc:flex-1 dc:min-h-0 dc:overflow-auto dc:p-4">
          <AnalysisDisplayConfigPanel
            chartType={chartType}
            displayConfig={displayConfig}
            colorPalette={colorPalette}
            onDisplayConfigChange={onDisplayConfigChange}
          />
        </div>
      ) : null}
    </div>
  )
})

export default FunnelModeContent
